import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import type { FormTemplateField as FormTemplateSection } from "@ServerTypes/forms/forms.schema";
import type { SelectedFileItem } from "@ServerTypes/folders/folders.schema";

/**
 * A folder's file-naming rule (`folderItemSchema.fileNomenclature`), exactly as the file-manager API
 * returns it. It is an OBJECT (`{ separator?, fields? }`), never a string — every consumer reads
 * `.separator` / `.fields` off it.
 */
type FileNomenclatureRule = NonNullable<SelectedFileItem["fileNomenclature"]>;

export interface FileUploadItem {
  /** The browser File object being uploaded. */
  file?: File;
  /** Error message from the Excel-import validation flow. */
  excelImportError?: string;
  isDeleted?: boolean;
  isFolder?: boolean;
  totalBytesUploaded?: number;
  size?: number;
  progress?: number;
  bytesUploaded?: number;
  location?: string[];
  documentType?: string[];
  isMetadataPending?: boolean;
  name?: string;
  type?: string;
  fileId?: string;
  folderId?: string;
  parentId?: string;
  spaceId?: string;
  hierarchyId?: string;
  status?: string;
  uploadId?: string;
  key?: string;
  url?: string;
  error?: string | null;
  errors?: string[];
  errorMessage?: string;
  progressCompleted?: boolean;
  progressStarted?: boolean;
  duplicateFileStatus?: {
    found?: boolean;
    newSuggestedName?: string;
    isDuplicateContainer?: boolean;
    noMatchingContainer?: boolean;
  } | null;
  isDuplicate?: boolean;
  customFieldsAutoFilled?: boolean;
  uploadLocation?: string | null;
  fileVersionId?: string;
  originalName?: string;
  extension?: string;
  mimeType?: string;
  fileNomenclature?: FileNomenclatureRule | null;
  isUploadAllowed?: boolean;
  formTemplateId?: string;
  folderPath?: string;
  batchId?: string;
  isFileVersion?: boolean;
  versionId?: string;
  uploadStartTime?: number;
  uploadEndTime?: number;
  isMetadataFilled?: boolean;
  currentFolderId?: string;
  controller?: AbortController | AbortController[] | null;
  triggerFileUpload?: boolean;
  id?: number | string;
  createdDate?: number;
  uploaded?: boolean;
  processing?: boolean;
  processed?: boolean;
  uploadTriggered?: boolean;
  /** Set once the post-upload custom-field/nomenclature follow-up has run for this row. */
  uploadCompleteProcessed?: boolean;
  revisionRejected?: boolean;
  nomenclature?: string[];
  isFileNomenclatureCorrect?: boolean;
  nomenclatureUpdated?: boolean;
  customFieldsUpdated?: boolean;
  customFieldsSaved?: boolean;
  isBulkEdit?: boolean;
  folder?: { fileNomenclature?: FileNomenclatureRule | null; [key: string]: unknown };
  matchedContainer?: { name?: string; errors?: string[]; [key: string]: unknown };
  matchedDirectImportContainer?: {
    containerData?: { errors?: string[]; [key: string]: unknown };
    [key: string]: unknown;
  };
}

interface UpdateFileUploadPayload {
  index: number;
  data: Partial<FileUploadItem>;
  spaceId?: string;
  hierarchyId?: string;
}

interface DocumentChangePayload {
  index: number;
  data: string;
}

export interface FileUploadFormTemplate {
  /**
   * The captured form-template SECTIONS (each carrying `questionFields`), not a flat field list. The
   * local `FormTemplateField` below described neither what the server returns nor what any consumer
   * reads, which is why every consumer cast around it.
   */
  template: FormTemplateSection[];
  formTemplateId?: string;
  name?: string;
  typeId?: string;
}

export interface FormTemplateField {
  key?: string;
  label?: string;
  type?: string;
  value?: string | number | boolean | null | string[];
  isMandatory?: boolean;
}

export interface FileUploadKeyData {
  fileUploadList?: FileUploadItem[];
  spaceId?: string;
  hierarchyId?: string;
  projectId?: string;
  workspaceId?: string;
  currentFiles?: FileUploadItem[];
  fileNomenclature?: FileNomenclatureRule | null;
  isUploadAllowed?: boolean;
  formTemplate?: FileUploadFormTemplate;
  isFileVersionUploading?: boolean;
  uploadFolderData?: { [k: string]: string | number | boolean | null | string[] };
  uploadSpaceId?: string;
  currentFolder?: { _id?: string; name?: string; hierarchyId?: string; spaceId?: string; filterPath?: string };
}

type FileUploadPropertiesState = Record<string, FileUploadKeyData>;

interface UpdateFileUploadPropertiesPayload {
  spaceId: string;
  hierarchyId: string;
  index: number;
  data: Partial<FileUploadItem>;
}

type FileUploadState = "CLOSED" | "MINIMISED" | "MAXIMISED";

export interface ExcelImportContainer {
  name?: string;
  hierarchyId?: string;
  spaceId?: string;
  fileId?: string;
  fileVersionId?: string;
}

export interface DirectImportCurrentData {
  folderId?: string;
  folderName?: string;
  spaceId?: string;
  hierarchyId?: string;
}

// Current file upload context properties
export interface CurrentFileUploadPropertiesState {
  formTemplate?: FileUploadFormTemplate;
  currentFolderRef?: { _id?: string; name?: string; hierarchyId?: string; spaceId?: string; filterPath?: string };
  isUploadAllowed?: boolean;
  fileNomenclature?: FileNomenclatureRule | null;
  workspaceId?: string;
  hierarchyId?: string;
  fileAllowedExtensions?: { [k: string]: boolean | number | string };
  currentFiles?: FileUploadItem[];
  draggedFiles?: File[];
  isFolderUpload?: boolean;
  fileUploadStateRef?: string | { current: string };
  isExcelImportFlow?: boolean;
  excelImportContainers?: ExcelImportContainer[];
  isDirectImportFlow?: boolean;
  directImportContainers?: ExcelImportContainer[];
  directImportCurrentData?: DirectImportCurrentData;
  directImportFolderFilesMap?: { [k: string]: string[] };
  formTemplateReceived?: boolean;
}

const intialFileUploadList: FileUploadItem[] = [];

const fileUploadListSlice = createSlice({
  name: "fileUploadList",
  initialState: intialFileUploadList,
  reducers: {
    // The payload is the raw upload-queue rows the FileManager builds, which carry more keys than
    // `FileUploadItem` declares; the existing assertion below is what pins them to the slice type.
    setFileUploadData: (state: FileUploadItem[], action: PayloadAction<object[]>) => {
      state.push(...(action.payload as FileUploadItem[]));
    },
    removeFileUploadData: (state: FileUploadItem[], action: PayloadAction<number>) => {
      const index = action.payload;
      if (state[index]) {
        state[index].isDeleted = true;
      }
    },
    removeAllFileUploadData: (state: FileUploadItem[]) => {
      state.forEach((item: FileUploadItem) => {
        item.isDeleted = true;
      });
    },
    updateFileUploadData: (state: FileUploadItem[], action: PayloadAction<UpdateFileUploadPayload>) => {
      const { index, data } = action.payload;
      if (!state[index]) return;
      if (state[index].isFolder && data.bytesUploaded) {
        state[index].totalBytesUploaded = (state[index].totalBytesUploaded ?? 0) + (data.bytesUploaded as number);
        const percentageComplete = Math.floor((state[index].totalBytesUploaded! * 100) / state[index].size!);
        state[index].progress = percentageComplete;
      }
      state[index] = { ...state[index], ...data };
    },
    documentChange: (state: FileUploadItem[], action: PayloadAction<DocumentChangePayload>) => {
      const { index, data } = action.payload;
      const item = state[index]!;
      const location = item.location;
      let documentType = [data];
      if (Array.isArray(item.documentType)) {
        documentType = [...item.documentType, data];
      }
      const isMetadataPending =
        !location ||
        (Array.isArray(location) && !location.length) ||
        (Array.isArray(documentType) && !documentType.length);
      state[index] = { ...item, documentType, isMetadataPending };
    },
    documentRemove: (state: FileUploadItem[], action: PayloadAction<DocumentChangePayload>) => {
      const { index, data } = action.payload;
      const item = state[index]!;
      const location = item.location;
      const documentType = (item.documentType ?? []).filter((d: string) => d !== data);
      const isMetadataPending =
        !location ||
        (Array.isArray(location) && !location.length) ||
        (Array.isArray(documentType) && !documentType.length);
      state[index] = { ...item, documentType, isMetadataPending };
    },
    locationChange: (state: FileUploadItem[], action: PayloadAction<DocumentChangePayload>) => {
      const { index, data } = action.payload;
      const item = state[index]!;
      const documentType = item.documentType;
      let location = [data];
      if (Array.isArray(item.location)) {
        location = [...item.location, data];
      }
      const isMetadataPending =
        (Array.isArray(location) && !location.length) ||
        !documentType ||
        (Array.isArray(documentType) && !documentType.length);
      state[index] = { ...item, location, isMetadataPending };
    },
    locationRemove: (state: FileUploadItem[], action: PayloadAction<DocumentChangePayload>) => {
      const { index, data } = action.payload;
      const item = state[index]!;
      const location = (item.location ?? []).filter((d: string) => d !== data);
      const documentType = item.documentType;
      const isMetadataPending =
        (Array.isArray(location) && !location.length) ||
        !documentType ||
        (Array.isArray(documentType) && !documentType.length);
      state[index] = { ...item, location, isMetadataPending };
    },
    emptyFileUploadData: () => intialFileUploadList,
  },
});

const fileUploadPropertiesSlice = createSlice({
  name: "fileUploadProperties",
  initialState: {} as FileUploadPropertiesState,
  reducers: {
    setFileUploadProperties: (state: FileUploadPropertiesState, action: PayloadAction<FileUploadPropertiesState>) => {
      Object.assign(state, action.payload);
    },
    updateFileUploadData: (
      state: FileUploadPropertiesState,
      action: PayloadAction<UpdateFileUploadPropertiesPayload>,
    ) => {
      const data = action.payload;
      if (data.spaceId && data.hierarchyId && state[`${data.spaceId}_${data.hierarchyId}`]) {
        const { fileUploadList = [] } = state[`${data.spaceId}_${data.hierarchyId}`]!;
        const index = data.index;
        const list = [...fileUploadList];
        if (list[index] && list[index].isFolder && data.data.bytesUploaded) {
          list[index].totalBytesUploaded = (list[index].totalBytesUploaded ?? 0) + (data.data.bytesUploaded as number);
          const percentageComplete = Math.floor((list[index].totalBytesUploaded! * 100) / list[index].size!);
          list[index].progress = percentageComplete;
        }
        list[index] = { ...list[index], ...data.data };
        state[`${data.spaceId}_${data.hierarchyId}`] = {
          ...state[`${data.spaceId}_${data.hierarchyId}`],
          fileUploadList: list,
        };
      }
    },
    emptyFileUploadData: () => ({}),
  },
});

const currentFileUploadPropertiesSlice = createSlice({
  name: "currentFileUploadProperties",
  initialState: {} as CurrentFileUploadPropertiesState,
  reducers: {
    setCurrentFileUploadProperties: (
      _state: CurrentFileUploadPropertiesState,
      action: PayloadAction<CurrentFileUploadPropertiesState>,
    ) => action.payload,
  },
});

const fileUploadStateSlice = createSlice({
  name: "fileUploadState",
  initialState: "CLOSED" as FileUploadState,
  reducers: {
    minimiseFileUpload: () => "MINIMISED" as FileUploadState,
    maximiseFileUpload: () => "MAXIMISED" as FileUploadState,
    closeFileUpload: () => "CLOSED" as FileUploadState,
  },
});

const isFileVersionUploadingSlice = createSlice({
  name: "isFileVersionUploading",
  initialState: false,
  reducers: {
    setIsFileVersionUploading: (_state: boolean, action: PayloadAction<boolean>) => action.payload,
  },
});

const fileVersionUploadStateSlice = createSlice({
  name: "fileVersionUploadState",
  initialState: "CLOSED" as FileUploadState,
  reducers: {
    minimiseFileVersionUpload: () => "MINIMISED" as FileUploadState,
    maximiseFileVersionUpload: () => "MAXIMISED" as FileUploadState,
    closeFileVersionUpload: () => "CLOSED" as FileUploadState,
  },
});

export const {
  setFileUploadData,
  removeFileUploadData,
  removeAllFileUploadData,
  updateFileUploadData,
  documentChange,
  documentRemove,
  locationChange,
  locationRemove,
  emptyFileUploadData,
} = fileUploadListSlice.actions;

export const {
  setFileUploadProperties,
  updateFileUploadData: updateFileUploadProperties,
  emptyFileUploadData: emptyFileUploadProperties,
} = fileUploadPropertiesSlice.actions;

export const { setCurrentFileUploadProperties } = currentFileUploadPropertiesSlice.actions;

export const { minimiseFileUpload, maximiseFileUpload, closeFileUpload } = fileUploadStateSlice.actions;

export const { setIsFileVersionUploading } = isFileVersionUploadingSlice.actions;

export const { minimiseFileVersionUpload, maximiseFileVersionUpload, closeFileVersionUpload } =
  fileVersionUploadStateSlice.actions;

export const fileUploadList = fileUploadListSlice.reducer;
export const fileUploadProperties = fileUploadPropertiesSlice.reducer;
export const currentFileUploadProperties = currentFileUploadPropertiesSlice.reducer;
export const fileUploadState = fileUploadStateSlice.reducer;
export const isFileVersionUploading = isFileVersionUploadingSlice.reducer;
export const fileVersionUploadState = fileVersionUploadStateSlice.reducer;
